import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import {
    Block,
    FlexDiv,
    Heading2,
    Heading3,
    TextDefault,
    TextLead,
    TextTiny,
    ThemedButton,
    palette,
} from '@awarego-ui/components';
import Link from '@mui/material/Link';
import { useStores } from '../../hooks/use-stores';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import SubjectRow from '../../components/manager/automation/subject-row';
import SubjectPreviewContainer from '../subjectPreview';
import RecommendationTrainingConfigModal, {
    parseRecommendationTrainingConfig,
} from './RecommendationTrainingConfigModal';

const CREATE_AS_ONE_KEY = 'ui.assessment.recommendations.create_as_one';
const INCLUDE_NOT_COMPLETED_KEY =
    'ui.assessment.recommendations.include_not_completed';
const SUBJECTS_BY_AREA_KEY = 'ui.assessment.recommendations.subjects_by_area';

function parseSubjectsByArea(raw) {
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
    if (typeof raw !== 'string') return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed
            : {};
    } catch {
        return {};
    }
}

function ThreatAreaSubjectsModal({
    open,
    onClose,
    threatAreaName,
    subjects,
    selectedIds,
    onSave,
    saving,
}) {
    const [draftIds, setDraftIds] = useState([]);
    const [previewSubjectId, setPreviewSubjectId] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);

    useEffect(() => {
        if (open) {
            setDraftIds([...(selectedIds || [])]);
            setPreviewSubjectId(null);
            setPendingAction(null);
        }
    }, [open, selectedIds]);

    useEffect(() => {
        if (!saving) setPendingAction(null);
    }, [saving]);

    const toggleOne = (id) => {
        setDraftIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleReset = () => {
        setPendingAction('reset');
        onSave(null);
    };

    const handleSave = () => {
        setPendingAction('save');
        onSave(draftIds);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>Select subjects — {threatAreaName}</DialogTitle>
                <DialogContent dividers>
                    <TextTiny block style={{ marginBottom: 12 }}>
                        Choose which subjects may be assigned for this threat
                        area in follow-up recommendations. Use Reset to All to
                        allow any catalog subject for this area.
                    </TextTiny>
                    <div
                        style={{
                            maxHeight: 480,
                            overflow: 'auto',
                            border: `1px solid ${palette.graphite.cloud}`,
                            borderRadius: 8,
                        }}
                    >
                        {subjects.length === 0 && (
                            <TextDefault
                                lighter
                                style={{ padding: 16, display: 'block' }}
                            >
                                No subjects found for this threat area in the
                                catalog.
                            </TextDefault>
                        )}
                        {subjects.map((subject) => (
                            <div
                                key={subject.id}
                                style={{
                                    borderBottom: `1px solid ${palette.graphite.cloud}`,
                                }}
                            >
                                <SubjectRow
                                    subject={subject}
                                    selection={draftIds}
                                    onVideoSelected={toggleOne}
                                    previewVideo={setPreviewSubjectId}
                                />
                            </div>
                        ))}
                    </div>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <ThemedButton
                        mode="secondary"
                        text="Reset to All"
                        disabled={saving}
                        $saving={saving && pendingAction === 'reset'}
                        onClick={handleReset}
                        $noTextTransform
                    />
                    <ThemedButton
                        mode="secondary"
                        text="Cancel"
                        disabled={saving}
                        onClick={onClose}
                        $noTextTransform
                    />
                    <ThemedButton
                        mode="primary"
                        text="Save"
                        disabled={saving || draftIds.length === 0}
                        $saving={saving && pendingAction === 'save'}
                        onClick={handleSave}
                        $noTextTransform
                    />
                </DialogActions>
            </Dialog>
            {previewSubjectId && (
                <SubjectPreviewContainer
                    isOpen={Boolean(previewSubjectId)}
                    onClose={() => setPreviewSubjectId(null)}
                    subjectId={previewSubjectId}
                    hideFooter={true}
                />
            )}
        </>
    );
}

function ContentSettings() {
    const { store, companyStore, catalogStore, commonStore } = useStores();
    const companyId = store.currentCompany?.company_id;
    const uiSettings = store.currentCompanyUISettings || {};

    const [subjectsByArea, setSubjectsByArea] = useState({});
    const [editingArea, setEditingArea] = useState(null);
    const [saving, setSaving] = useState(false);
    const [trainingConfigOpen, setTrainingConfigOpen] = useState(false);

    const createAsOne = uiSettings[CREATE_AS_ONE_KEY] !== '0';
    const includeNotCompleted =
        uiSettings[INCLUDE_NOT_COMPLETED_KEY] === '1';
    const trainingConfigDraft = parseRecommendationTrainingConfig(uiSettings);

    useEffect(() => {
        if (companyId) {
            catalogStore.loadThreatAreaCatalog(companyId);
        }
    }, [companyId, catalogStore]);

    useEffect(() => {
        setSubjectsByArea(parseSubjectsByArea(uiSettings[SUBJECTS_BY_AREA_KEY]));
    }, [uiSettings[SUBJECTS_BY_AREA_KEY]]);

    const areas = catalogStore.availableSubjectsByThreatArea || [];

    const areaRows = useMemo(() => {
        return areas.map((area) => {
            const ids = subjectsByArea[area.threatAreaName];
            const isAll = !ids || !Array.isArray(ids) || ids.length === 0;
            const selectedTitles = isAll
                ? []
                : (area.subjects || [])
                      .filter((s) => ids.includes(s.id))
                      .map((s) => s.title || `Subject ${s.id}`);
            return {
                threatAreaName: area.threatAreaName,
                subjects: area.subjects || [],
                isAll,
                selectedIds: isAll ? [] : ids,
                summary: isAll
                    ? 'All'
                    : selectedTitles.length
                      ? selectedTitles.join(', ')
                      : `${ids.length} subject(s)`,
            };
        });
    }, [areas, subjectsByArea]);

    const persistSubjectsByArea = async (nextMap) => {
        setSaving(true);
        try {
            const cleaned = {};
            Object.entries(nextMap).forEach(([area, ids]) => {
                if (Array.isArray(ids) && ids.length > 0) {
                    cleaned[area] = ids.map(Number).filter((n) => n > 0);
                }
            });
            await companyStore.saveRecommendationSettings(companyId, {
                [SUBJECTS_BY_AREA_KEY]: cleaned,
            });
            setSubjectsByArea(cleaned);
            commonStore.success('Recommendation subjects saved.');
            setEditingArea(null);
        } catch (e) {
            commonStore.error(
                companyStore.error || e?.message || 'Failed to save'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleToggleFlag = async (key, checked, defaultWhenUnset) => {
        // Store explicit '1' / '0'
        let value;
        if (key === CREATE_AS_ONE_KEY) {
            // default true when unset — checked means '1' or omit; we always store explicit
            value = checked ? '1' : '0';
        } else if (key === INCLUDE_NOT_COMPLETED_KEY) {
            value = checked ? '1' : '0';
        } else {
            value = checked ? '1' : '0';
        }
        void defaultWhenUnset;
        setSaving(true);
        try {
            await companyStore.saveRecommendationSettings(companyId, {
                [key]: value,
            });
            commonStore.success('Setting saved.');
        } catch (e) {
            commonStore.error(
                companyStore.error || e?.message || 'Failed to save'
            );
        } finally {
            setSaving(false);
        }
    };

    const editingRow = editingArea
        ? areaRows.find((r) => r.threatAreaName === editingArea)
        : null;

    if (!companyId) {
        return <ThemedSpinner />;
    }

    return (
        <Block>
            <Heading2 mb={8} displayBlock>
                Content settings
            </Heading2>
            <TextDefault lighter mb={24} displayBlock>
                Configure assessment follow-up recommendation content for this
                company.
            </TextDefault>

            <Heading3 mb={8} mt={16} displayBlock>
                Recommendation settings
            </Heading3>
            <TextTiny lighter block style={{ marginBottom: 16 }}>
                These options apply when custom assessment recommendations are
                enabled for the company.
            </TextTiny>

            <FlexDiv
                column
                gap={8}
                style={{
                    padding: '16px 0',
                    borderBottom: `1px solid ${palette.graphite.cloud}`,
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={createAsOne}
                            disabled={saving}
                            onChange={(e) =>
                                handleToggleFlag(
                                    CREATE_AS_ONE_KEY,
                                    e.target.checked,
                                    true
                                )
                            }
                        />
                    }
                    label={
                        <FlexDiv column>
                            <TextLead bold>
                                Show “Create as one training” section
                            </TextLead>
                            <TextTiny lighter>
                                When enabled, Recommendation includes the single
                                combined training plan (subject grid and Create
                                as one training…). When disabled, only Proposed
                                trainings is shown.
                            </TextTiny>
                        </FlexDiv>
                    }
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeNotCompleted}
                            disabled={saving}
                            onChange={(e) =>
                                handleToggleFlag(
                                    INCLUDE_NOT_COMPLETED_KEY,
                                    e.target.checked,
                                    false
                                )
                            }
                        />
                    }
                    label={
                        <FlexDiv column>
                            <TextLead bold>
                                Include “Not completed” training group
                            </TextLead>
                            <TextTiny lighter>
                                When enabled, employees who did not finish the
                                assessment get their own proposed training with
                                all follow-up modules. When disabled, they are
                                skipped.
                            </TextTiny>
                        </FlexDiv>
                    }
                />
            </FlexDiv>

            <FlexDiv
                column
                gap={8}
                style={{
                    padding: '16px 0',
                    borderBottom: `1px solid ${palette.graphite.cloud}`,
                }}
            >
                <FlexDiv justifyBetween alignCenter gap={16} $noWrap>
                    <FlexDiv column>
                        <TextLead bold>Recommendation training</TextLead>
                        <TextTiny lighter>
                            Defaults used when creating follow-up trainings from
                            assessment recommendations (name, due date,
                            reminders).
                        </TextTiny>
                        <TextTiny lighter style={{ marginTop: 4 }}>
                            Name: {trainingConfigDraft.nameTemplate} · List:{' '}
                            {trainingConfigDraft.listNameTemplate} · Send:{' '}
                            {trainingConfigDraft.scheduleType === 0
                                ? 'Scheduled'
                                : trainingConfigDraft.scheduleType === 2
                                  ? 'Manual'
                                  : 'Now'}{' '}
                            · Due: {trainingConfigDraft.dueDays} days ·
                            Reminders:{' '}
                            {trainingConfigDraft.remindersEnabled
                                ? 'On'
                                : 'Off'}
                        </TextTiny>
                    </FlexDiv>
                    <ThemedButton
                        mode="secondary"
                        size="tiny"
                        $noTextTransform
                        text="Configure"
                        onClick={() => setTrainingConfigOpen(true)}
                    />
                </FlexDiv>
            </FlexDiv>

            <Heading3 mb={8} mt={32} displayBlock>
                Allowed subjects by threat area
            </Heading3>
            <TextTiny lighter block style={{ marginBottom: 16 }}>
                By default each threat area allows <strong>All</strong> catalog
                subjects. Edit to restrict follow-up recommendations to selected
                subjects for that area.
            </TextTiny>

            {catalogStore.loadingCatalog && <ThemedSpinner />}

            {!catalogStore.loadingCatalog && areaRows.length === 0 && (
                <TextDefault lighter>
                    No threat areas found in the content catalog.
                </TextDefault>
            )}

            {!catalogStore.loadingCatalog &&
                areaRows.map((row) => (
                    <FlexDiv
                        key={row.threatAreaName}
                        pt={16}
                        pb={16}
                        borderBottom
                        fullWidth
                        alignCenter
                    >
                        <FlexDiv flexGrow={1} column>
                            <TextLead bold>{row.threatAreaName}</TextLead>
                            <TextTiny lighter>
                                {row.subjects.length}{' '}
                                {row.subjects.length === 1
                                    ? 'subject'
                                    : 'subjects'}{' '}
                                in catalog
                            </TextTiny>
                        </FlexDiv>
                        <FlexDiv flexGrow={2} column ml={24}>
                            <TextTiny bold>{row.summary}</TextTiny>
                            {!row.isAll && (
                                <TextTiny lighter>
                                    Restricted to selected subjects
                                </TextTiny>
                            )}
                        </FlexDiv>
                        <FlexDiv justifyEnd flexGrow={1}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() =>
                                    setEditingArea(row.threatAreaName)
                                }
                            >
                                <TextLead themed bold link>
                                    Edit
                                </TextLead>
                            </Link>
                        </FlexDiv>
                    </FlexDiv>
                ))}

            <ThreatAreaSubjectsModal
                open={Boolean(editingArea)}
                onClose={() => setEditingArea(null)}
                threatAreaName={editingArea || ''}
                subjects={editingRow?.subjects || []}
                selectedIds={
                    editingRow?.isAll
                        ? (editingRow?.subjects || []).map((s) => s.id)
                        : editingRow?.selectedIds || []
                }
                saving={saving}
                onSave={async (idsOrNull) => {
                    const next = { ...subjectsByArea };
                    if (idsOrNull == null) {
                        delete next[editingArea];
                    } else {
                        next[editingArea] = idsOrNull;
                    }
                    await persistSubjectsByArea(next);
                }}
            />

            <RecommendationTrainingConfigModal
                open={trainingConfigOpen}
                onClose={() => setTrainingConfigOpen(false)}
                initial={trainingConfigDraft}
                saving={saving}
                onSave={async (payload) => {
                    setSaving(true);
                    try {
                        await companyStore.saveRecommendationSettings(
                            companyId,
                            {
                                'ui.assessment.recommendations.training.name_template':
                                    payload.nameTemplate,
                                'ui.assessment.recommendations.training.list_name_template':
                                    payload.listNameTemplate,
                                'ui.assessment.recommendations.training.due_days':
                                    String(payload.dueDays),
                                'ui.assessment.recommendations.training.sending_time':
                                    payload.sendingTime,
                                'ui.assessment.recommendations.training.schedule_type':
                                    String(payload.scheduleType),
                                'ui.assessment.recommendations.training.reminders_enabled':
                                    payload.remindersEnabled ? '1' : '0',
                                'ui.assessment.recommendations.training.reminders_settings':
                                    payload.remindersSettings,
                            }
                        );
                        commonStore.success(
                            'Recommendation training settings saved.'
                        );
                        setTrainingConfigOpen(false);
                    } catch (e) {
                        commonStore.error(
                            companyStore.error ||
                                e?.message ||
                                'Failed to save'
                        );
                    } finally {
                        setSaving(false);
                    }
                }}
            />
        </Block>
    );
}

export default observer(ContentSettings);
